import apiService from "@/core/services/apiService";

export type State = { classes: object };
import {Module, VuexModule, Mutation, Action} from "vuex-module-decorators";
import {Actions, Mutations} from "@/store/enums/store.enums";
import {CoursesActions, CoursesMutation} from "@/modules/courses/store/enums";
import store from "@/store";

@Module
export default class Store extends VuexModule {
    renderKey = 1;
    listParams = {
        page: 1,
        results: 10,
        search: null,
        sort: [],
        filter: {}
    };
    listCache = [];
    listTotal = 0;

    get getCoursesRenderKey() {
        return 'courses-' + this.renderKey;
    }

    get getCoursesLastListResult() {
        return this.listCache;
    }

    get getCoursesLastListTotal() {
        return this.listTotal;
    }

    @Mutation
    [CoursesMutation.SET_RENDER_UPDATE]() {
        this.renderKey++;
    }

    @Mutation
    [CoursesMutation.SET_LIST_RESULT]({listParams, listTotal, listCache}) {
        this.listParams = listParams;
        this.listCache = listCache;
        this.listTotal = listTotal;
    }

    @Action
    [CoursesActions.REFRESH_LIST]() {
        this.context.dispatch(CoursesActions.FETCH_LIST, this.listParams);
    }

    @Action
    [CoursesActions.FETCH_LIST](params) {

        return new Promise<void>((resolve, reject) => {

            apiService.post("courses/list", params).then((response) => {

                const data = response.data || null;

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                // return results into storage for reactive update if needed //
                this.context.commit(CoursesMutation.SET_LIST_RESULT, {
                    listParams: params,
                    listTotal: data.results,
                    listCache: data.result
                });
                resolve();

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [CoursesActions.GET_ITEM](detailId) {

        return new Promise<void>((resolve, reject) => {

            apiService.get(`courses/get/${detailId}`).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                // return results to dispatcher without mutation //
                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [CoursesActions.CREATE_ITEM](payload) {

        return new Promise((resolve, reject) => {

            const {data} = payload;

            apiService.post('courses/create/', data).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                // flag cached keep alive comp to render new //
                this.context.commit(CoursesMutation.SET_RENDER_UPDATE);

                // refresh list in background//
                this.context.dispatch(CoursesActions.REFRESH_LIST);

                // return results to dispatcher without mutation //
                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [CoursesActions.UPDATE_ITEM](payload) {

        return new Promise((resolve, reject) => {

            const {editId, data} = payload;

            apiService.put(`courses/edit/${editId}`, data).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                // refresh list in background//
                this.context.dispatch(CoursesActions.REFRESH_LIST);

                // return results to dispatcher without mutation //
                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [CoursesActions.DELETE_ITEM](editId) {

        return new Promise((resolve, reject) => {

            apiService.delete(`courses/delete/${editId}`).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                // refresh list in background//
                this.context.dispatch(CoursesActions.REFRESH_LIST);

                // return results to dispatcher without mutation //
                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    // ── Dropdown Caches ──────────────────────────────────────── //

    typesCache = [];
    expertisesCache = [];
    locationsCache = [];
    partnersCache = [];
    instructorsCache = [];
    subscriptionPackagesCache = [];

    get getCoursesTypes() { return this.typesCache; }
    get getCoursesExpertises() { return this.expertisesCache; }
    get getCoursesLocations() { return this.locationsCache; }
    get getCoursesPartners() { return this.partnersCache; }
    get getCoursesInstructors() { return this.instructorsCache; }
    get getCoursesSubscriptionPackages() { return this.subscriptionPackagesCache; }

    // ── Description ──────────────────────────────────────────── //

    @Action
    [CoursesActions.UPDATE_DESCRIPTION](payload) {
        return new Promise((resolve, reject) => {
            const {editId, data} = payload;
            apiService.post(`courses/update-description/${editId}`, data).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    // ── Types CRUD ───────────────────────────────────────────── //

    @Action
    [CoursesActions.FETCH_TYPES](params?) {
        return new Promise((resolve, reject) => {
            apiService.get("courses/types", params).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [CoursesActions.GET_TYPE](typeId) {
        return new Promise((resolve, reject) => {
            apiService.get(`courses/get-type/${typeId}`).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [CoursesActions.CREATE_TYPE](payload) {
        return new Promise((resolve, reject) => {
            apiService.post("courses/create-type", payload).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [CoursesActions.UPDATE_TYPE](payload) {
        return new Promise((resolve, reject) => {
            const {editId, data} = payload;
            apiService.put(`courses/edit-type/${editId}`, data).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [CoursesActions.DELETE_TYPE](typeId) {
        return new Promise((resolve, reject) => {
            apiService.delete(`courses/delete-type/${typeId}`).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    // ── Expertises CRUD ──────────────────────────────────────── //

    @Action
    [CoursesActions.GET_EXPERTISE](expertiseId) {
        return new Promise((resolve, reject) => {
            apiService.get(`courses/get-expertise/${expertiseId}`).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [CoursesActions.FETCH_EXPERTISES](params?) {
        return new Promise((resolve, reject) => {
            apiService.get("courses/expertises", params).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [CoursesActions.CREATE_EXPERTISE](payload) {
        return new Promise((resolve, reject) => {
            apiService.post("courses/create-expertise", payload).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [CoursesActions.UPDATE_EXPERTISE](payload) {
        return new Promise((resolve, reject) => {
            const {editId, data} = payload;
            apiService.put(`courses/edit-expertise/${editId}`, data).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [CoursesActions.DELETE_EXPERTISE](expertiseId) {
        return new Promise((resolve, reject) => {
            apiService.delete(`courses/delete-expertise/${expertiseId}`).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    // ── Locations CRUD ───────────────────────────────────────── //

    @Action
    [CoursesActions.FETCH_LOCATIONS](params?) {
        return new Promise((resolve, reject) => {
            apiService.get("courses/locations", params).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [CoursesActions.CREATE_LOCATION](payload) {
        return new Promise((resolve, reject) => {
            apiService.post("courses/create-location", payload).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [CoursesActions.UPDATE_LOCATION](payload) {
        return new Promise((resolve, reject) => {
            const {editId, data} = payload;
            apiService.put(`courses/edit-location/${editId}`, data).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [CoursesActions.DELETE_LOCATION](locationId) {
        return new Promise((resolve, reject) => {
            apiService.delete(`courses/delete-location/${locationId}`).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    // ── Partners CRUD ────────────────────────────────────────── //

    @Action
    [CoursesActions.FETCH_PARTNERS](params?) {
        return new Promise((resolve, reject) => {
            apiService.get("courses/partners", params).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [CoursesActions.CREATE_PARTNER](payload) {
        return new Promise((resolve, reject) => {
            apiService.post("courses/create-partner", payload).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [CoursesActions.UPDATE_PARTNER](payload) {
        return new Promise((resolve, reject) => {
            const {editId, data} = payload;
            apiService.put(`courses/edit-partner/${editId}`, data).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [CoursesActions.DELETE_PARTNER](partnerId) {
        return new Promise((resolve, reject) => {
            apiService.delete(`courses/delete-partner/${partnerId}`).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    // ── Trainer-Partner Details (neuer partnerGet/partnerUpdate) ──────────────── //

    @Action
    [CoursesActions.GET_PARTNER](partnerId) {
        return new Promise((resolve, reject) => {
            apiService.get(`courses/partner-get/${partnerId}`).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [CoursesActions.UPDATE_PARTNER_DETAILS](payload) {
        return new Promise((resolve, reject) => {
            const {partnerId, data: partnerData} = payload;
            apiService.put(`courses/partner-update/${partnerId}`, partnerData).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [CoursesActions.WAITING_BOOKING](bookingId) {
        return new Promise((resolve, reject) => {
            apiService.put(`courses/waiting-booking/${bookingId}`, {}).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [CoursesActions.REMIND_BOOKING](bookingId) {
        return new Promise((resolve, reject) => {
            apiService.put(`courses/remind-booking/${bookingId}`, {}).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [CoursesActions.FETCH_DASHBOARD_BOOKINGS]() {
        return new Promise((resolve, reject) => {
            apiService.get("courses/dashboard-bookings").then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [CoursesActions.UPLOAD_EXPERTISE_FILE](payload) {
        return new Promise((resolve, reject) => {
            apiService.post("courses/upload-expertise-file", payload).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [CoursesActions.DELETE_EXPERTISE_FILE](payload) {
        return new Promise((resolve, reject) => {
            apiService.delete(`courses/delete-expertise-file/${payload.expertiseId}/${payload.fileType}`).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [CoursesActions.DOWNLOAD_INVOICE](bookingId) {
        return new Promise((resolve, reject) => {
            apiService.query(`courses/download-invoice/${bookingId}`, { responseType: 'blob' }).then((response) => {
                const blob = new Blob([response.data], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `invoice-${bookingId}.pdf`;
                link.click();
                window.URL.revokeObjectURL(url);
                resolve(true);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    // ── Instructors + Subscription Packages ──────────────────── //

    @Action
    [CoursesActions.FETCH_INSTRUCTORS]() {
        return new Promise((resolve, reject) => {
            apiService.get("courses/instructors").then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [CoursesActions.FETCH_SUBSCRIPTION_PACKAGES]() {
        return new Promise((resolve, reject) => {
            apiService.get("courses/subscription-packages").then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    // ── Bookings ─────────────────────────────────────────────── //

    @Action
    [CoursesActions.FETCH_BOOKINGS](courseId) {
        return new Promise((resolve, reject) => {
            apiService.get(`courses/bookings/${courseId}`).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [CoursesActions.ADD_BOOKING](payload) {
        return new Promise((resolve, reject) => {
            apiService.post("courses/add-booking", payload).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [CoursesActions.CONFIRM_BOOKING](bookingId) {
        return new Promise((resolve, reject) => {
            apiService.put(`courses/confirm-booking/${bookingId}`, {}).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [CoursesActions.REFUSE_BOOKING](bookingId) {
        return new Promise((resolve, reject) => {
            apiService.put(`courses/refuse-booking/${bookingId}`, {}).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [CoursesActions.REMOVE_BOOKING](bookingId) {
        return new Promise((resolve, reject) => {
            apiService.delete(`courses/remove-booking/${bookingId}`).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    // ── Mail + PDF + Invoice ───────────────────────────────────── //

    @Action
    [CoursesActions.SEND_MAIL](payload) {
        return new Promise((resolve, reject) => {
            apiService.post("courses/send-mail", payload).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [CoursesActions.GET_EMAIL_TEMPLATE]({courseId, mailType, userId}) {
        return new Promise((resolve, reject) => {
            const url = `courses/get-email-template/${courseId}/${mailType}` + (userId ? `?userId=${userId}` : '');
            apiService.get(url).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [CoursesActions.DOWNLOAD_PDF]({courseId, pdfType}) {
        return new Promise((resolve, reject) => {
            apiService.query(`courses/download-pdf/${courseId}/${pdfType}`, { responseType: 'blob' }).then((response) => {
                const blob = new Blob([response.data], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${pdfType}-${courseId}.pdf`;
                link.click();
                window.URL.revokeObjectURL(url);
                resolve(true);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [CoursesActions.CREATE_INVOICE](bookingId) {
        return new Promise((resolve, reject) => {
            apiService.post(`courses/create-invoice/${bookingId}`, {}).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    // GET_SETTINGS/UPDATE_SETTINGS wurden 2026-07 ersatzlos entfernt: die Kurs-Settings-Felder
    // las keine aktive Logik — Rechnungs-Texte/Aussteller kommen aus den Accounting-Settings. //

    // ── Export ───────────────────────────────────────────────────── //

    @Action
    [CoursesActions.EXPORT_PARTICIPANTS](courseId) {
        return new Promise((resolve, reject) => {
            apiService.get(`courses/export-participants/${courseId}`).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [CoursesActions.EXPORT_REGISTRATIONS](courseId) {
        return new Promise((resolve, reject) => {
            apiService.get(`courses/export-registrations/${courseId}`).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    // ── Documents ───────────────────────────────────────────────── //

    @Action
    [CoursesActions.FETCH_DOCUMENTS](courseId) {
        return new Promise((resolve, reject) => {
            apiService.get(`courses/documents/${courseId}`).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [CoursesActions.UPLOAD_DOCUMENT](payload) {
        return new Promise((resolve, reject) => {
            apiService.post("courses/upload-document", payload).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [CoursesActions.ADD_DOCUMENT](payload) {
        return new Promise((resolve, reject) => {
            apiService.post("courses/add-document", payload).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [CoursesActions.DELETE_DOCUMENT]({courseId, documentId}) {
        return new Promise((resolve, reject) => {
            apiService.delete(`courses/delete-document/${courseId}/${documentId}`).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    // ── State Updates ───────────────────────────────────────────── //

    @Action
    [CoursesActions.SET_PRESENCE_STATE]({courseId, bookingId, state}) {
        return new Promise((resolve, reject) => {
            apiService.put(`courses/set-presence-state/${courseId}/${bookingId}`, {state} as any).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [CoursesActions.SET_VISIBLE_STATE]({courseId, bookingId, state}) {
        return new Promise((resolve, reject) => {
            apiService.put(`courses/set-visible-state/${courseId}/${bookingId}`, {state} as any).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    // ── Copy ────────────────────────────────────────────────────── //

    @Action
    [CoursesActions.COPY_COURSE](sourceId) {
        return new Promise((resolve, reject) => {
            apiService.post(`courses/copy-course/${sourceId}`, {}).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    // ── Participant Inline Edit ──────────────────────────────────── //

    @Action
    [CoursesActions.SET_PARTICIPANT_NAME]({courseId, bookingId, value}) {
        return new Promise((resolve, reject) => {
            apiService.put(`courses/set-participant-name/${courseId}/${bookingId}`, {value} as any).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [CoursesActions.SET_PARTICIPANT_ROLE]({courseId, bookingId, value}) {
        return new Promise((resolve, reject) => {
            apiService.put(`courses/set-participant-role/${courseId}/${bookingId}`, {value} as any).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [CoursesActions.SET_PARTICIPANT_EMAIL]({courseId, bookingId, value}) {
        return new Promise((resolve, reject) => {
            apiService.put(`courses/set-participant-email/${courseId}/${bookingId}`, {value} as any).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    // ── Image Upload ─────────────────────────────────────────── //

    @Action
    [CoursesActions.UPLOAD_IMAGE](payload) {
        return new Promise((resolve, reject) => {
            apiService.post("courses/upload-image", payload).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [CoursesActions.DELETE_IMAGE](payload) {
        return new Promise((resolve, reject) => {
            apiService.delete("courses/delete-image/" + payload.courseId + "/" + payload.imageType).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

}
